import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  calculateIncomeDetails,
  FilingStatus,
  TaxProfile,
  OREGON_TAX_BRACKETS_2025,
} from "../../lib/taxCalculatorUtils";
import { expenseAPI, pageShareAPI } from "../../services/api/api";
import { Expense } from "../../lib/types";
import { getCachedUser } from "../../services/supabase/authStorage";
import { addDays } from "date-fns";
import { expenseUtils } from "../../lib";
import { getTodayLocal } from "../../lib/dateUtils";
import {
  calculateCapitalGain,
  calculateFederalLtcgTax,
} from "../../lib/housingForecastUtils";
import {
  onTaxProfilesChanged,
  taxProfilesDB,
} from "../../services/database/taxProfilesDB";
import {
  HousingForecastProfile,
  HousingForecastSettings,
  housingForecastProfilesDB,
  onHousingForecastProfilesChanged,
} from "../../services/database/housingForecastProfilesDB";
import PageShareModal from "../common/PageShareModal";
import { useShareAction } from "../../contexts/ShareActionContext";

type RealEstateAsset = {
  id: string;
  name: string;
  address: string;
  acres: number;
  purchaseDate: string; // yyyy-mm-dd
  purchasePrice: number;
  improvements: number;
};

const DEFAULT_SUNRIVER_ASSET: Omit<RealEstateAsset, "id"> = {
  name: "Sunriver Land",
  address: "17035 Kasserman Drive, Sunriver, Oregon 97707",
  acres: 1.01,
  purchaseDate: "2020-05-01",
  // This is the purchase price (basis), not the current market value.
  purchasePrice: 32500,
  improvements: 0,
};

type SaleInputs = {
  saleDate: string; // yyyy-mm-dd
  salePrice: number;
  brokerCommissionPct: number; // % of sale price, min $5,000
  closingCostsPctByCategory: Record<string, number>; // % of sale price by category
  otherSellingCosts: number;
  currentMortgageBalance: number;
  mortgageInterest: number;
  reinvest1031: boolean;
  includeNIIT: boolean;
};

type ProjectionInputs = {
  mode: "renting" | "buying";
  loanTypeId: string;
  avgRent: number;
  homePrice: number;
  downPaymentPct: number; // 0..1
  mortgageAprPct: number;
  mortgageTermYears: number;
  propertyTaxRatePct: number; // annual property tax rate
  homeownersInsuranceAnnual: number; // annual premium
  pmiRatePct: number; // annual PMI rate (applied when DP < 20%)
  hoaMonthly: number; // monthly HOA dues
  useAutoMonthlyExpenses: boolean;
  monthlyExpensesOverride: number;
};

const LEGACY_STORAGE_KEY = "housing_forecast_v1";

const formatCurrency = (value: number) => expenseUtils.formatCurrency(value);

const formatSignedCurrency = (value: number) => {
  if (!Number.isFinite(value)) return "-";
  const abs = Math.abs(value);
  const formatted = formatCurrency(abs);
  return value < 0 ? `-${formatted}` : formatted;
};

const signedColor = (value: number) => {
  if (value > 0) return "success.main";
  if (value < 0) return "error.main";
  return "text.primary";
};

const SignedMoney: React.FC<{ value: number; emphasize?: boolean }> = ({
  value,
  emphasize,
}) => {
  return (
    <Typography
      variant="body2"
      sx={{
        textAlign: "right",
        fontWeight: emphasize ? 900 : undefined,
        color: signedColor(value),
      }}
    >
      {formatSignedCurrency(value)}
    </Typography>
  );
};

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const parseNumber = (raw: string, fallback: number) => {
  const next = Number(raw);
  return Number.isFinite(next) ? next : fallback;
};

const parseRatePercent = (raw: string, fallback: number) => {
  const sanitized = String(raw).replace(/[^\d.]/g, "");
  const [wholeRaw = "", decimalRaw = ""] = sanitized.split(".");
  const whole = wholeRaw.slice(0, 6);
  const availableDecimalDigits = Math.max(0, 6 - whole.length);
  const decimal = decimalRaw.slice(0, availableDecimalDigits);
  const normalized = decimalRaw.length > 0 ? `${whole}.${decimal}` : whole;
  const next = Number(normalized);
  if (!Number.isFinite(next)) return fallback;
  return clampNumber(next, 0, 50);
};

const formatLocalYmd = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const generateId = () =>
  `asset_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const CLOSING_COST_CATEGORIES: Array<{ key: string; label: string }> = [
  { key: "title_escrow", label: "Title & Escrow" },
  { key: "recording_transfer", label: "Recording / Transfer" },
  { key: "attorney", label: "Attorney" },
  { key: "other", label: "Other" },
];

const NIIT_THRESHOLDS: Record<FilingStatus, number> = {
  single: 200000,
  married_jointly: 250000,
  head_of_household: 200000,
};

const calculateProgressiveTax = (
  taxableIncome: number,
  brackets: Array<{ rate: number; lowerBound: number; upperBound: number }>
) => {
  let total = 0;
  for (const bracket of brackets) {
    if (taxableIncome <= bracket.lowerBound) break;
    const inBracket =
      Math.min(taxableIncome, bracket.upperBound) - bracket.lowerBound;
    total += inBracket * bracket.rate;
  }
  return total;
};

const monthsToYearsMonths = (months: number) => {
  const m = Math.max(0, Math.ceil(months));
  const years = Math.floor(m / 12);
  const rem = m % 12;
  return { years, months: rem };
};

const computeNext30DaysExpensesTotal = (expenses: Expense[]) => {
  const today = getTodayLocal();
  const end = addDays(today, 30);
  return expenses
    .filter((e) => !e.isSkipped)
    .filter((e) => !e.isPaid)
    .filter((e) => e.dueDate && e.dueDate >= today && e.dueDate <= end)
    .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
};

const getDefaultSaleInputs = (): SaleInputs => ({
  saleDate: formatLocalYmd(getTodayLocal()),
  salePrice: 100000,
  brokerCommissionPct: 5,
  closingCostsPctByCategory: {
    // Defaults are editable; these are typical "ballpark" closing buckets.
    title_escrow: 0.8,
    recording_transfer: 0.2,
    attorney: 0.1,
    other: 0.4,
  },
  otherSellingCosts: 0,
  currentMortgageBalance: 24500,
  mortgageInterest: 1800,
  reinvest1031: false, // conservative default chosen
  includeNIIT: false,
});

const getDefaultProjectionInputs = (): ProjectionInputs => ({
  mode: "renting",
  loanTypeId: "conventional_30",
  avgRent: 3250,
  homePrice: 550000,
  downPaymentPct: 0.1,
  mortgageAprPct: 7.0,
  mortgageTermYears: 30,
  propertyTaxRatePct: 0,
  homeownersInsuranceAnnual: 0,
  pmiRatePct: 0,
  hoaMonthly: 0,
  useAutoMonthlyExpenses: true,
  monthlyExpensesOverride: 0,
});

type LoanTypeOption = {
  id: string;
  label: string;
  termYears: number;
  noteRatePct: number;
  aprPct: number;
};

const LOAN_TYPE_OPTIONS: LoanTypeOption[] = [
  {
    id: "conforming_15_fixed",
    label: "15 Year Fixed (Conforming)",
    termYears: 15,
    noteRatePct: 5.75,
    aprPct: 6.509,
  },
  {
    id: "conventional_30",
    label: "30 Year Fixed Conventional",
    termYears: 30,
    noteRatePct: 6.125,
    aprPct: 6.19,
  },
  {
    id: "fha_30",
    label: "30 Year FHA",
    termYears: 30,
    noteRatePct: 5.5,
    aprPct: 6.255,
  },
  {
    id: "va_30",
    label: "30 Year VA",
    termYears: 30,
    noteRatePct: 5.5,
    aprPct: 5.769,
  },
  {
    id: "usda_30",
    label: "30 Year USDA Rural Home Purchase",
    termYears: 30,
    noteRatePct: 5.875,
    aprPct: 6.234,
  },
  {
    id: "manufactured_conventional_30",
    label: "30 Year Conventional Manufactured Home",
    termYears: 30,
    noteRatePct: 6.375,
    aprPct: 6.442,
  },
  {
    id: "manufactured_fha_30",
    label: "30 Year FHA Manufactured Home",
    termYears: 30,
    noteRatePct: 5.875,
    aprPct: 6.712,
  },
  {
    id: "manufactured_va_30",
    label: "30 Year VA Manufactured Home",
    termYears: 30,
    noteRatePct: 6.125,
    aprPct: 6.407,
  },
  {
    id: "custom",
    label: "Custom",
    termYears: 30,
    noteRatePct: 0,
    aprPct: 0,
  },
];

const generateHousingProfileId = (): string =>
  `housing_profile_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const HousingForecast: React.FC = () => {
  const userId = getCachedUser()?.id || "unknown";
  const [searchParams] = useSearchParams();
  const { setShareHandler } = useShareAction();

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [pendingShareId, setPendingShareId] = useState<string | null>(null);

  const [taxProfiles, setTaxProfiles] = useState<TaxProfile[]>([]);
  const [taxProfileId, setTaxProfileId] = useState<string>("");

  const [housingProfiles, setHousingProfiles] = useState<
    HousingForecastProfile[]
  >([]);
  const [housingProfileId, setHousingProfileId] = useState<string>("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingNewProfile, setSavingNewProfile] = useState(false);

  const [sharedMode, setSharedMode] = useState(false);
  const [sharedTaxProfile, setSharedTaxProfile] = useState<TaxProfile | null>(
    null
  );

  const defaultAssets: RealEstateAsset[] = useMemo(
    () => [
      {
        id: generateId(),
        ...DEFAULT_SUNRIVER_ASSET,
      },
    ],
    []
  );

  const [assets, setAssets] = useState<RealEstateAsset[]>(defaultAssets);
  const [selectedAssetId, setSelectedAssetId] = useState<string>(
    defaultAssets[0]?.id || ""
  );

  const [saleInputs, setSaleInputs] = useState<SaleInputs>(() =>
    getDefaultSaleInputs()
  );

  const [projectionInputs, setProjectionInputs] = useState<ProjectionInputs>(
    () => getDefaultProjectionInputs()
  );

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [autoMonthlyExpenses, setAutoMonthlyExpenses] = useState<number>(0);
  const [expensesLoading, setExpensesLoading] = useState<boolean>(true);

  useEffect(() => {
    setShareHandler(() => {
      setShareDialogOpen(true);
    });
    return () => setShareHandler(null);
  }, [setShareHandler]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const remote = await taxProfilesDB.getAll();
      if (!mounted) return;
      setTaxProfiles(remote);
      if (!taxProfileId && remote.length > 0) {
        setTaxProfileId(remote[0].id);
      }
    };
    void load();
    const unsub = onTaxProfilesChanged(() => void load());
    return () => {
      mounted = false;
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (taxProfiles.length === 0) return;
    if (taxProfiles.some((p) => p.id === taxProfileId)) return;
    setTaxProfileId(taxProfiles[0].id);
  }, [taxProfiles, taxProfileId]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      let remote = await housingForecastProfilesDB.getAll();

      // One-time migration: if remote is empty, try to import legacy localStorage assets.
      if (remote.length === 0) {
        const legacyKey = `${LEGACY_STORAGE_KEY}:${userId}`;
        const raw = localStorage.getItem(legacyKey);
        if (raw) {
          try {
            const parsed = JSON.parse(raw) as { assets?: RealEstateAsset[] };
            const legacyAssets = Array.isArray(parsed.assets)
              ? parsed.assets
              : [];
            if (legacyAssets.length > 0) {
              const migratedAssets = legacyAssets.map((a) => {
                const looksLikeOldDefault =
                  a.name === DEFAULT_SUNRIVER_ASSET.name &&
                  a.address === DEFAULT_SUNRIVER_ASSET.address &&
                  a.acres === DEFAULT_SUNRIVER_ASSET.acres &&
                  a.purchaseDate === DEFAULT_SUNRIVER_ASSET.purchaseDate &&
                  a.improvements === 0 &&
                  a.purchasePrice === 100000;
                if (!looksLikeOldDefault) return a;
                return {
                  ...a,
                  purchasePrice: DEFAULT_SUNRIVER_ASSET.purchasePrice,
                };
              });

              const imported: HousingForecastProfile = {
                id: generateHousingProfileId(),
                name: "Imported",
                settings: {
                  version: 1,
                  taxProfileId: null,
                  assets: migratedAssets,
                  selectedAssetId:
                    migratedAssets[0]?.id || migratedAssets[0]?.id || "",
                  saleInputs: getDefaultSaleInputs(),
                  projectionInputs: getDefaultProjectionInputs(),
                } as HousingForecastSettings,
                createdAt: Date.now(),
              };

              await housingForecastProfilesDB.upsert(imported);
              try {
                localStorage.removeItem(legacyKey);
              } catch {
                // ignore
              }

              remote = await housingForecastProfilesDB.getAll();
            }
          } catch {
            // ignore
          }
        }
      }

      if (!mounted) return;
      setHousingProfiles(remote);
    };
    void load();
    const unsub = onHousingForecastProfilesChanged(() => void load());
    return () => {
      mounted = false;
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (housingProfiles.length === 0) return;
    if (housingProfiles.some((p) => p.id === housingProfileId)) return;
    setHousingProfileId(housingProfiles[0].id);
  }, [housingProfiles, housingProfileId]);

  useEffect(() => {
    const load = async () => {
      setExpensesLoading(true);

      // Try to hydrate from existing Expenses cache first.
      const expensesCacheKey = `expenses_page_cache_v1:${userId}`;
      const raw = localStorage.getItem(expensesCacheKey);
      if (raw) {
        try {
          const cached = JSON.parse(raw) as { expenses?: Expense[] };
          if (Array.isArray(cached.expenses)) {
            const formatted = cached.expenses.map((e) => ({
              ...e,
              dueDate:
                typeof e.dueDate === "string" ? new Date(e.dueDate) : e.dueDate,
            }));
            setExpenses(formatted);
            setAutoMonthlyExpenses(computeNext30DaysExpensesTotal(formatted));
          }
        } catch {
          // ignore
        }
      }

      try {
        const all = await expenseAPI.getAll();
        const formatted = all.map((e) => ({
          ...e,
          dueDate:
            typeof e.dueDate === "string" ? new Date(e.dueDate) : e.dueDate,
        }));
        setExpenses(formatted);
        setAutoMonthlyExpenses(computeNext30DaysExpensesTotal(formatted));
      } finally {
        setExpensesLoading(false);
      }
    };

    void load();
  }, [userId]);

  const selectedHousingProfile = useMemo(
    () => housingProfiles.find((p) => p.id === housingProfileId) || null,
    [housingProfiles, housingProfileId]
  );

  const normalizeHousingSettings = useCallback(
    (raw: any): HousingForecastSettings => {
      const nextAssets: RealEstateAsset[] = Array.isArray(raw?.assets)
        ? raw.assets
        : defaultAssets;
      const safeAssets = nextAssets.length > 0 ? nextAssets : defaultAssets;

      const saleDefaults = getDefaultSaleInputs();
      const projectionDefaults = getDefaultProjectionInputs();

      const mergedSaleInputs: any = {
        ...saleDefaults,
        ...(typeof raw?.saleInputs === "object" && raw.saleInputs
          ? raw.saleInputs
          : {}),
      };

      // Back-compat: if older profiles stored flat $ amounts, approximate
      // percentages using the stored sale price (or default sale price).
      const salePriceForPct = Math.max(
        1,
        Number(mergedSaleInputs.salePrice || saleDefaults.salePrice || 1)
      );
      if (typeof mergedSaleInputs.brokerCommissionPct !== "number") {
        const legacy = Number(mergedSaleInputs.brokerCommission || 0);
        if (legacy > 0) {
          mergedSaleInputs.brokerCommissionPct =
            (legacy / salePriceForPct) * 100;
        }
      }
      if (
        !mergedSaleInputs.closingCostsPctByCategory ||
        typeof mergedSaleInputs.closingCostsPctByCategory !== "object"
      ) {
        const legacy = Number(mergedSaleInputs.closingCosts || 0);
        if (legacy > 0) {
          const totalPct = (legacy / salePriceForPct) * 100;
          // Preserve the default breakdown ratios; scale totals to match legacy.
          const defaultTotal = Object.values(
            saleDefaults.closingCostsPctByCategory || {}
          ).reduce((sum, v) => sum + Number(v || 0), 0);
          const scale = defaultTotal > 0 ? totalPct / defaultTotal : 0;
          const next: Record<string, number> = {};
          Object.entries(saleDefaults.closingCostsPctByCategory || {}).forEach(
            ([k, v]) => {
              next[k] = Number(v || 0) * scale;
            }
          );
          mergedSaleInputs.closingCostsPctByCategory = next;
        } else {
          mergedSaleInputs.closingCostsPctByCategory =
            saleDefaults.closingCostsPctByCategory;
        }
      }

      return {
        version: 1,
        taxProfileId:
          typeof raw?.taxProfileId === "string" ? raw.taxProfileId : null,
        assets: safeAssets,
        selectedAssetId:
          typeof raw?.selectedAssetId === "string" && raw.selectedAssetId
            ? raw.selectedAssetId
            : safeAssets[0]?.id || "",
        saleInputs: mergedSaleInputs,
        projectionInputs: {
          ...projectionDefaults,
          ...(typeof raw?.projectionInputs === "object" && raw.projectionInputs
            ? raw.projectionInputs
            : {}),
          // Back-compat migration from legacy single monthly field.
          hoaMonthly:
            typeof raw?.projectionInputs?.hoaMonthly === "number"
              ? raw.projectionInputs.hoaMonthly
              : Number(raw?.projectionInputs?.otherMonthlyHousingCosts || 0),
        },
      };
    },
    [defaultAssets]
  );

  const buildCurrentHousingSettings =
    useCallback((): HousingForecastSettings => {
      return {
        version: 1,
        taxProfileId: taxProfileId || null,
        assets,
        selectedAssetId,
        saleInputs,
        projectionInputs,
      };
    }, [assets, projectionInputs, saleInputs, selectedAssetId, taxProfileId]);

  const applySharedPayload = useCallback(
    (payload: any) => {
      const normalized = normalizeHousingSettings(payload?.settings);
      setAssets(normalized.assets as RealEstateAsset[]);
      setSelectedAssetId(normalized.selectedAssetId);
      setSaleInputs(normalized.saleInputs as SaleInputs);
      setProjectionInputs(normalized.projectionInputs as ProjectionInputs);

      const sharedIncomeInfo = payload?.taxProfile?.incomeInfo;
      if (sharedIncomeInfo) {
        const profile: TaxProfile = {
          id: "shared_tax_profile",
          name:
            typeof payload?.taxProfile?.name === "string"
              ? payload.taxProfile.name
              : "Shared",
          incomeInfo: sharedIncomeInfo,
          createdAt: Date.now(),
        };
        setSharedTaxProfile(profile);
      } else {
        setSharedTaxProfile(null);
      }
      setSharedMode(true);
    },
    [normalizeHousingSettings]
  );

  const handleUnlockShare = useCallback(async () => {
    if (!pendingShareId || !unlockPassword.trim()) {
      setUnlockError("Password required.");
      return;
    }
    setUnlockError("");
    try {
      const result = await pageShareAPI.validate(
        pendingShareId,
        unlockPassword.trim()
      );
      applySharedPayload(result.payload);
      setUnlockDialogOpen(false);
      setPendingShareId(null);
      setUnlockPassword("");
    } catch (error) {
      console.error("[HousingForecast] Failed to unlock share", error);
      setUnlockError("Invalid password.");
    }
  }, [applySharedPayload, pendingShareId, unlockPassword]);

  useEffect(() => {
    const shareId = searchParams.get("shareId");
    if (!shareId) return;

    // Prevent DB-backed profile state from overwriting shared payload.
    setSharedMode(true);

    const loadShare = async () => {
      try {
        const info = await pageShareAPI.getInfo(shareId);
        if (info.pageType !== "housing") {
          setUnlockError("This share link is not for Housing Forecast.");
          return;
        }
        if (info.requiresPassword) {
          setPendingShareId(shareId);
          setUnlockDialogOpen(true);
          return;
        }
        const result = await pageShareAPI.validate(shareId);
        applySharedPayload(result.payload);
      } catch (error) {
        console.error("[HousingForecast] Failed to load share", error);
        setUnlockError("Failed to load shared data.");
      }
    };
    void loadShare();
  }, [applySharedPayload, searchParams]);

  useEffect(() => {
    if (sharedMode) return;
    if (!selectedHousingProfile) return;
    const settings = normalizeHousingSettings(selectedHousingProfile.settings);
    setAssets(settings.assets as RealEstateAsset[]);
    setSelectedAssetId(settings.selectedAssetId);
    setSaleInputs(settings.saleInputs as SaleInputs);
    setProjectionInputs(settings.projectionInputs as ProjectionInputs);
    if (settings.taxProfileId) {
      setTaxProfileId(settings.taxProfileId);
    }
  }, [normalizeHousingSettings, selectedHousingProfile, sharedMode]);

  const selectedTaxProfile = useMemo(() => {
    if (sharedMode && sharedTaxProfile) return sharedTaxProfile;
    return taxProfiles.find((p) => p.id === taxProfileId) || null;
  }, [sharedMode, sharedTaxProfile, taxProfiles, taxProfileId]);

  const incomeDetails = useMemo(() => {
    if (!selectedTaxProfile) return null;
    return calculateIncomeDetails(selectedTaxProfile.incomeInfo);
  }, [selectedTaxProfile]);

  const selectedAsset = useMemo(
    () => assets.find((a) => a.id === selectedAssetId) || null,
    [assets, selectedAssetId]
  );

  const exchangeTimeline = useMemo(() => {
    const saleDate = saleInputs.saleDate;
    if (!saleDate) return null;
    const start = new Date(`${saleDate}T00:00:00`);
    const identificationDeadline = addDays(start, 45);
    const completionDeadline = addDays(start, 180);
    const today = getTodayLocal();
    return {
      saleDate,
      identificationDeadline,
      completionDeadline,
      isPastIdentification: today > identificationDeadline,
      isPastCompletion: today > completionDeadline,
    };
  }, [saleInputs.saleDate]);

  const ownership = useMemo(() => {
    if (!selectedAsset) return null;
    const purchase = new Date(`${selectedAsset.purchaseDate}T00:00:00Z`);
    const now = new Date();
    const days = Math.max(
      0,
      Math.floor((now.getTime() - purchase.getTime()) / 86400000)
    );
    const years = Math.floor(days / 365.25);
    const months = Math.floor((days - years * 365.25) / 30.44);
    return { years, months };
  }, [selectedAsset]);

  const monthlyTakeHome = incomeDetails?.monthlyIncome.netIncome ?? 0;
  const yearlyTaxableIncomeBase =
    incomeDetails?.yearlyIncome.taxableIncome ?? 0;
  const yearlyGrossBase = incomeDetails?.yearlyIncome.grossIncome ?? 0;
  const filingStatus = selectedTaxProfile?.incomeInfo.filingStatus ?? "single";

  const effectiveMonthlyExpenses = projectionInputs.useAutoMonthlyExpenses
    ? autoMonthlyExpenses
    : projectionInputs.monthlyExpensesOverride;

  const brokerCommissionPct = useMemo(() => {
    return clampNumber(Number(saleInputs.brokerCommissionPct || 0), 0, 25);
  }, [saleInputs.brokerCommissionPct]);

  const brokerCommissionAmount = useMemo(() => {
    const computed = (saleInputs.salePrice * brokerCommissionPct) / 100;
    return Math.max(5000, Math.max(0, computed));
  }, [saleInputs.salePrice, brokerCommissionPct]);

  const closingCostsByCategory = useMemo(() => {
    const pctMap = saleInputs.closingCostsPctByCategory || {};
    return CLOSING_COST_CATEGORIES.map((c) => {
      const pct = clampNumber(Number(pctMap[c.key] || 0), 0, 25);
      const amount = Math.max(0, (saleInputs.salePrice * pct) / 100);
      return { ...c, pct, amount };
    });
  }, [saleInputs.closingCostsPctByCategory, saleInputs.salePrice]);

  const closingCostsTotal = useMemo(() => {
    return closingCostsByCategory.reduce((sum, c) => sum + c.amount, 0);
  }, [closingCostsByCategory]);

  const sellingCostsTotal =
    brokerCommissionAmount + closingCostsTotal + saleInputs.otherSellingCosts;

  const capitalGain = useMemo(() => {
    if (!selectedAsset) {
      return { amountRealized: 0, adjustedBasis: 0, rawGain: 0, gain: 0 };
    }
    return calculateCapitalGain({
      salePrice: saleInputs.salePrice,
      sellingCostsTotal,
      purchasePrice: selectedAsset.purchasePrice,
      improvements: selectedAsset.improvements,
    });
  }, [saleInputs.salePrice, sellingCostsTotal, selectedAsset]);

  const { amountRealized, adjustedBasis, rawGain, gain } = capitalGain;

  const federalLtcgTax = useMemo(() => {
    if (saleInputs.reinvest1031) return 0;
    return calculateFederalLtcgTax({
      filingStatus,
      baseTaxableIncome: yearlyTaxableIncomeBase,
      longTermCapitalGains: gain,
    });
  }, [saleInputs.reinvest1031, filingStatus, yearlyTaxableIncomeBase, gain]);

  const oregonCapitalGainsTax = useMemo(() => {
    if (saleInputs.reinvest1031) return 0;
    // Approx: incremental Oregon income tax on added capital gains using the existing bracket model.
    const base = calculateProgressiveTax(
      yearlyTaxableIncomeBase,
      OREGON_TAX_BRACKETS_2025
    );
    const withGains = calculateProgressiveTax(
      yearlyTaxableIncomeBase + gain,
      OREGON_TAX_BRACKETS_2025
    );
    return Math.max(0, withGains - base);
  }, [saleInputs.reinvest1031, yearlyTaxableIncomeBase, gain]);

  const niitTax = useMemo(() => {
    if (saleInputs.reinvest1031) return 0;
    if (!saleInputs.includeNIIT) return 0;
    const threshold = NIIT_THRESHOLDS[filingStatus];
    const magiApprox = Math.max(0, yearlyGrossBase) + Math.max(0, gain);
    const excess = Math.max(0, magiApprox - threshold);
    const netInvestmentIncomeApprox = Math.max(0, gain);
    const base = Math.min(excess, netInvestmentIncomeApprox);
    return base * 0.038;
  }, [
    saleInputs.reinvest1031,
    saleInputs.includeNIIT,
    filingStatus,
    yearlyGrossBase,
    gain,
  ]);

  const totalTax = federalLtcgTax + oregonCapitalGainsTax + niitTax;
  const netProceeds = Math.max(
    0,
    saleInputs.salePrice - sellingCostsTotal - totalTax
  );
  const mortgagePayoffTotal =
    saleInputs.currentMortgageBalance + saleInputs.mortgageInterest;
  const netCashAfterMortgage = Math.max(0, netProceeds - mortgagePayoffTotal);

  // Use "net cash" as the amount actually available after taxes + selling costs + mortgage payoff.
  const netFromSale = netCashAfterMortgage;

  const monthsOfRent =
    projectionInputs.avgRent > 0 ? netFromSale / projectionInputs.avgRent : 0;
  const selectedLoanType =
    LOAN_TYPE_OPTIONS.find(
      (option) => option.id === projectionInputs.loanTypeId
    ) || LOAN_TYPE_OPTIONS.find((option) => option.id === "custom")!;

  const downPaymentGoal =
    projectionInputs.homePrice *
    clampNumber(projectionInputs.downPaymentPct, 0, 1);
  const downPaymentRatio = clampNumber(projectionInputs.downPaymentPct, 0, 1);
  const requiresPmi = downPaymentRatio < 0.2;

  const loanAmount = Math.max(0, projectionInputs.homePrice - downPaymentGoal);
  const monthlyMortgagePayment = useMemo(() => {
    const principal = loanAmount;
    const years = Math.max(0, Math.round(projectionInputs.mortgageTermYears));
    const months = years * 12;
    if (principal <= 0 || months <= 0) return 0;
    const monthlyRate = projectionInputs.mortgageAprPct / 100 / 12;
    if (!Number.isFinite(monthlyRate) || monthlyRate <= 0) {
      return principal / months;
    }
    const pow = Math.pow(1 + monthlyRate, -months);
    const payment = principal * (monthlyRate / (1 - pow));
    return Number.isFinite(payment) ? payment : 0;
  }, [
    loanAmount,
    projectionInputs.mortgageAprPct,
    projectionInputs.mortgageTermYears,
  ]);

  const monthlyPmi = useMemo(() => {
    if (!requiresPmi || loanAmount <= 0) return 0;
    return (
      (loanAmount * (clampNumber(projectionInputs.pmiRatePct, 0, 25) / 100)) /
      12
    );
  }, [loanAmount, projectionInputs.pmiRatePct, requiresPmi]);

  const monthlyPropertyTax = useMemo(() => {
    const annualRate = clampNumber(projectionInputs.propertyTaxRatePct, 0, 25);
    if (projectionInputs.homePrice <= 0 || annualRate <= 0) return 0;
    return (projectionInputs.homePrice * (annualRate / 100)) / 12;
  }, [projectionInputs.homePrice, projectionInputs.propertyTaxRatePct]);

  const monthlyHomeownersInsurance = useMemo(() => {
    const annual = Math.max(0, projectionInputs.homeownersInsuranceAnnual || 0);
    return annual / 12;
  }, [projectionInputs.homeownersInsuranceAnnual]);

  const monthlyHousingPaymentBuying =
    monthlyMortgagePayment +
    monthlyPmi +
    monthlyPropertyTax +
    monthlyHomeownersInsurance +
    Math.max(0, projectionInputs.hoaMonthly || 0);

  const monthlyNetSavingsRenting =
    monthlyTakeHome - effectiveMonthlyExpenses - projectionInputs.avgRent;

  const monthlyNetSavingsBuying =
    monthlyTakeHome - effectiveMonthlyExpenses - monthlyHousingPaymentBuying;

  const monthsToDownPayment = useMemo(() => {
    if (downPaymentGoal <= 0) return 0;
    if (netFromSale >= downPaymentGoal) return 0;
    if (monthlyNetSavingsRenting <= 0) return null;
    return (downPaymentGoal - netFromSale) / monthlyNetSavingsRenting;
  }, [downPaymentGoal, netFromSale, monthlyNetSavingsRenting]);

  const timeToDownPayment =
    monthsToDownPayment === null
      ? null
      : monthsToYearsMonths(monthsToDownPayment);

  const mortgageReserve24Months = monthlyHousingPaymentBuying * 24;

  const sources = [
    {
      label: "IRS Topic 409: capital gains rates and thresholds (2025)",
      href: "https://www.irs.gov/taxtopics/tc409",
    },
    {
      label:
        "IRS Pub 544: dispositions of property, gain/loss basics, 1031 background",
      href: "https://www.irs.gov/publications/p544",
    },
    {
      label: "IRS Pub 523: selling your home (Section 121 exclusion rules)",
      href: "https://www.irs.gov/publications/p523",
    },
    {
      label: "IRS: Like-kind exchanges (Section 1031) real estate tax tips",
      href: "https://www.irs.gov/businesses/small-businesses-self-employed/like-kind-exchanges-real-estate-tax-tips",
    },
    {
      label: "IRS Instructions for Form 8824: like-kind exchanges reporting",
      href: "https://www.irs.gov/instructions/i8824",
    },
    {
      label:
        "IRS Instructions for Form 8960: Net Investment Income Tax (NIIT) 3.8%",
      href: "https://www.irs.gov/instructions/i8960",
    },
    {
      label:
        "Oregon DOR Publication OR-17 (2024): Oregon taxes capital gain included on federal return",
      href: "https://www.oregon.gov/dor/forms/FormsPubs/publication-or-17_101-431_2024.pdf",
    },
    {
      label: "Oregon DOR: personal income tax rate charts (2025)",
      href: "https://www.oregon.gov/dor/programs/individuals/pages/pit.aspx",
    },
    {
      label:
        "Oregon tax expenditure report (2023-25): gain on like-kind exchanges (1031) treatment",
      href: "https://www.oregon.gov/dor/programs/gov-research/Documents/te2325_web.pdf",
    },
  ];

  const updateAsset = (patch: Partial<RealEstateAsset>) => {
    setAssets((prev) =>
      prev.map((a) => (a.id === selectedAssetId ? { ...a, ...patch } : a))
    );
  };

  return (
    <Box sx={{ maxWidth: 980, mx: "auto", py: 3, px: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        Housing Forecast
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Conservative default: no Section 121 exclusion, no 1031, treat gains as
        taxable. All inputs are editable.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Housing Profiles
          </Typography>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="housing-profile-label">Profile</InputLabel>
            <Select
              labelId="housing-profile-label"
              label="Profile"
              value={housingProfileId}
              disabled={sharedMode}
              onChange={(e) => setHousingProfileId(String(e.target.value))}
            >
              {housingProfiles.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            size="small"
            disabled={sharedMode}
            onClick={() => setSaveDialogOpen(true)}
          >
            Save As
          </Button>

          {housingProfileId && (
            <>
              <Button
                variant="outlined"
                size="small"
                disabled={sharedMode || savingProfile}
                onClick={async () => {
                  if (!housingProfileId) return;
                  const existing = housingProfiles.find(
                    (p) => p.id === housingProfileId
                  );
                  if (!existing) return;
                  setSavingProfile(true);
                  try {
                    await housingForecastProfilesDB.upsert({
                      ...existing,
                      settings: buildCurrentHousingSettings(),
                      createdAt: existing.createdAt || Date.now(),
                    });
                  } finally {
                    setSavingProfile(false);
                  }
                }}
                startIcon={
                  savingProfile ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SaveIcon fontSize="small" />
                  )
                }
              >
                Save
              </Button>
              <Tooltip title="Delete this profile" arrow>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={sharedMode}
                    onClick={async () => {
                      if (!housingProfileId) return;
                      await housingForecastProfilesDB.delete(housingProfileId);
                      setHousingProfileId("");
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}

          {sharedMode && (
            <Typography variant="caption" color="text.secondary">
              Shared view: saving is disabled.
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Save Housing Profile Dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Housing Profile</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Profile Name"
            fullWidth
            variant="outlined"
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newProfileName.trim()) {
                // handled by Save button
              }
            }}
            placeholder="e.g., Sunriver Sale, Keep Land, etc."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newProfileName.trim() || savingNewProfile}
            onClick={async () => {
              if (!newProfileName.trim()) return;
              const profile: HousingForecastProfile = {
                id: generateHousingProfileId(),
                name: newProfileName.trim(),
                settings: buildCurrentHousingSettings(),
                createdAt: Date.now(),
              };
              setSavingNewProfile(true);
              try {
                const saved = await housingForecastProfilesDB.upsert(profile);
                if (saved) {
                  setHousingProfileId(saved.id);
                }
                setNewProfileName("");
                setSaveDialogOpen(false);
              } finally {
                setSavingNewProfile(false);
              }
            }}
            startIcon={savingNewProfile ? <CircularProgress size={16} /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Unlock Dialog */}
      <Dialog
        open={unlockDialogOpen}
        onClose={() => setUnlockDialogOpen(false)}
      >
        <DialogTitle>Enter Password</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={unlockPassword}
            onChange={(e) => setUnlockPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                void handleUnlockShare();
              }
            }}
          />
          {unlockError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {unlockError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleUnlockShare()}>
            Unlock
          </Button>
        </DialogActions>
      </Dialog>

      {!selectedTaxProfile && !sharedMode && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No Income Tax Profiles found. Create one in Tools → Tax Calculator →
          Profile, then return here and select it.
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            Income Tax Profile (Net)
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              mt: 1,
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(260px, 1.2fr) repeat(3, minmax(180px, 1fr))",
              },
              alignItems: "end",
            }}
          >
            <FormControl fullWidth size="small">
              <InputLabel id="tax-profile-label">Tax Profile</InputLabel>
              <Select
                labelId="tax-profile-label"
                label="Tax Profile"
                value={taxProfileId}
                disabled={sharedMode}
                onChange={(e) => setTaxProfileId(String(e.target.value))}
              >
                {taxProfiles.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Monthly Take-Home (from Tax Calculator)
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>
                {formatCurrency(monthlyTakeHome)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Filing Status
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>{filingStatus}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Yearly Taxable Income (Used for LTCG)
              </Typography>
              <Typography sx={{ fontWeight: 800 }}>
                {formatCurrency(yearlyTaxableIncomeBase)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            Real Estate Asset
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="asset-label">Asset</InputLabel>
            <Select
              labelId="asset-label"
              label="Asset"
              value={selectedAssetId}
              onChange={(e) => setSelectedAssetId(String(e.target.value))}
            >
              {assets.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedAsset && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                size="small"
                label="Name"
                value={selectedAsset.name}
                onChange={(e) => updateAsset({ name: e.target.value })}
              />
              <TextField
                size="small"
                label="Address"
                value={selectedAsset.address}
                onChange={(e) => updateAsset({ address: e.target.value })}
              />
              <TextField
                size="small"
                label="Acres"
                value={String(selectedAsset.acres)}
                onChange={(e) =>
                  updateAsset({
                    acres: parseNumber(e.target.value, selectedAsset.acres),
                  })
                }
              />
              <TextField
                size="small"
                label="Purchase Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={selectedAsset.purchaseDate}
                onChange={(e) => updateAsset({ purchaseDate: e.target.value })}
              />
              <TextField
                size="small"
                label={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    Purchase Price (Basis)
                    <Tooltip
                      title={
                        "This is what you paid (basis). If you set this equal to your sale price, estimated gain may be $0, which results in $0 capital gains tax."
                      }
                      placement="top"
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                    </Tooltip>
                  </Box>
                }
                value={String(selectedAsset.purchasePrice)}
                onChange={(e) =>
                  updateAsset({
                    purchasePrice: parseNumber(
                      e.target.value,
                      selectedAsset.purchasePrice
                    ),
                  })
                }
              />
              <TextField
                size="small"
                label={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    Improvements (Basis)
                    <Tooltip
                      title={
                        "Improvements are capital costs that generally increase your cost basis (ex: grading, driveway, permits, utility hookups, permanent structures). This is not tax advice; basis rules can be nuanced."
                      }
                      placement="top"
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                    </Tooltip>
                  </Box>
                }
                value={String(selectedAsset.improvements)}
                onChange={(e) =>
                  updateAsset({
                    improvements: parseNumber(
                      e.target.value,
                      selectedAsset.improvements
                    ),
                  })
                }
              />
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Ownership Length (from purchase date)
            </Typography>
            <Typography sx={{ fontWeight: 800 }}>
              {ownership
                ? `${ownership.years} years, ${ownership.months} months`
                : "-"}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            Sale Analysis
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.25fr 0.75fr" },
              gap: 2,
              alignItems: "start",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                size="small"
                label="Sale Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={saleInputs.saleDate}
                onChange={(e) =>
                  setSaleInputs((prev) => ({
                    ...prev,
                    saleDate: e.target.value,
                  }))
                }
              />
              <TextField
                size="small"
                label="Sale Price"
                value={String(saleInputs.salePrice)}
                onChange={(e) =>
                  setSaleInputs((prev) => ({
                    ...prev,
                    salePrice: parseNumber(e.target.value, prev.salePrice),
                  }))
                }
              />
              <TextField
                size="small"
                label="Broker Commission (%)"
                value={String(saleInputs.brokerCommissionPct)}
                onChange={(e) =>
                  setSaleInputs((prev) => ({
                    ...prev,
                    brokerCommissionPct: parseNumber(
                      e.target.value,
                      prev.brokerCommissionPct
                    ),
                  }))
                }
                helperText={`Min $5,000. Current: ${formatCurrency(
                  brokerCommissionAmount
                )}`}
              />
              <TextField
                size="small"
                label="Other Selling Costs"
                value={String(saleInputs.otherSellingCosts)}
                onChange={(e) =>
                  setSaleInputs((prev) => ({
                    ...prev,
                    otherSellingCosts: parseNumber(
                      e.target.value,
                      prev.otherSellingCosts
                    ),
                  }))
                }
              />
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  p: 1.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Closing Costs (% of sale price)
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 1.25,
                  }}
                >
                  {closingCostsByCategory.map((c) => (
                    <Box
                      key={c.key}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <TextField
                        size="small"
                        label={`${c.label} (%)`}
                        value={String(
                          saleInputs.closingCostsPctByCategory?.[c.key] ?? c.pct
                        )}
                        onChange={(e) =>
                          setSaleInputs((prev) => ({
                            ...prev,
                            closingCostsPctByCategory: {
                              ...(prev.closingCostsPctByCategory || {}),
                              [c.key]: parseNumber(
                                e.target.value,
                                prev.closingCostsPctByCategory?.[c.key] ?? 0
                              ),
                            },
                          }))
                        }
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 800, textAlign: "right" }}
                      >
                        {formatCurrency(c.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Total closing costs: {formatCurrency(closingCostsTotal)}
                </Typography>
              </Box>
              <TextField
                size="small"
                label="Current Mortgage Balance"
                value={String(saleInputs.currentMortgageBalance)}
                onChange={(e) =>
                  setSaleInputs((prev) => ({
                    ...prev,
                    currentMortgageBalance: parseNumber(
                      e.target.value,
                      prev.currentMortgageBalance
                    ),
                  }))
                }
              />
              <TextField
                size="small"
                label="Mortgage Interest"
                value={String(saleInputs.mortgageInterest)}
                onChange={(e) =>
                  setSaleInputs((prev) => ({
                    ...prev,
                    mortgageInterest: parseNumber(
                      e.target.value,
                      prev.mortgageInterest
                    ),
                  }))
                }
              />
              <FormControl fullWidth size="small">
                <InputLabel id="reinvest-label">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    Reinvest (1031)
                    <Tooltip
                      title={
                        "A 1031 like-kind exchange can defer capital gains tax when you exchange qualifying investment/real estate property for another like-kind property and meet IRS rules (including timing). This tool only estimates; it does not validate eligibility."
                      }
                      placement="top"
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                    </Tooltip>
                  </Box>
                </InputLabel>
                <Select
                  labelId="reinvest-label"
                  label="Reinvest (1031)"
                  value={saleInputs.reinvest1031 ? "yes" : "no"}
                  onChange={(e) =>
                    setSaleInputs((prev) => ({
                      ...prev,
                      reinvest1031: String(e.target.value) === "yes",
                    }))
                  }
                >
                  <MenuItem value="no">No (taxable gain)</MenuItem>
                  <MenuItem value="yes">Yes (defer gain via 1031)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="niit-label">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    NIIT (3.8%)
                    <Tooltip
                      title={
                        "Net Investment Income Tax (NIIT) is an additional 3.8% federal tax that may apply to some investment income (including some capital gains) above certain thresholds. This is an estimate and uses an approximate MAGI calculation."
                      }
                      placement="top"
                      arrow
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                    </Tooltip>
                  </Box>
                </InputLabel>
                <Select
                  labelId="niit-label"
                  label="NIIT 3.8%"
                  value={saleInputs.includeNIIT ? "on" : "off"}
                  onChange={(e) =>
                    setSaleInputs((prev) => ({
                      ...prev,
                      includeNIIT: String(e.target.value) === "on",
                    }))
                  }
                >
                  <MenuItem value="off">Off</MenuItem>
                  <MenuItem value="on">On (estimate)</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "action.hover",
                border: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                Totals
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75 }}
              >
                Cash Equation
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  rowGap: 0.75,
                  columnGap: 2,
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                }}
              >
                <Typography variant="body2">Sale Price</Typography>
                <SignedMoney value={saleInputs.salePrice} />

                <Typography variant="body2">
                  <Tooltip
                    title={
                      "Selling costs (broker commissions, closing costs, etc.) generally reduce the amount realized and therefore reduce capital gain."
                    }
                    placement="top"
                    arrow
                  >
                    <Box
                      component="span"
                      sx={{ textDecoration: "underline dotted" }}
                    >
                      Selling Costs
                    </Box>
                  </Tooltip>
                </Typography>
                <SignedMoney value={-sellingCostsTotal} />

                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Divider />
                </Box>

                <Typography variant="body2">
                  ={" "}
                  <Tooltip
                    title={
                      "Amount realized is commonly sale price minus selling costs (simplified)."
                    }
                    placement="top"
                    arrow
                  >
                    <Box
                      component="span"
                      sx={{ textDecoration: "underline dotted" }}
                    >
                      Amount Realized
                    </Box>
                  </Tooltip>
                </Typography>
                <SignedMoney value={amountRealized} emphasize />

                <Typography variant="body2">
                  <Tooltip
                    title={
                      "This is an estimate of tax on the estimated gain. If 1031 is set to Yes, this tool assumes gain is deferred (tax shown as $0)."
                    }
                    placement="top"
                    arrow
                  >
                    <Box
                      component="span"
                      sx={{ textDecoration: "underline dotted" }}
                    >
                      Taxes
                    </Box>
                  </Tooltip>
                </Typography>
                <SignedMoney value={-totalTax} />

                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Divider />
                </Box>

                <Typography variant="body2">
                  ={" "}
                  <Tooltip
                    title={
                      "Net proceeds here means sale price minus selling costs minus estimated taxes. This is a cash-style subtotal, not reduced by basis."
                    }
                    placement="top"
                    arrow
                  >
                    <Box
                      component="span"
                      sx={{ textDecoration: "underline dotted" }}
                    >
                      Net Proceeds
                    </Box>
                  </Tooltip>
                </Typography>
                <SignedMoney value={netProceeds} emphasize />

                <Typography variant="body2">
                  <Tooltip
                    title={
                      "Mortgage payoff affects your net cash, but it typically does not change the capital gain calculation."
                    }
                    placement="top"
                    arrow
                  >
                    <Box
                      component="span"
                      sx={{ textDecoration: "underline dotted" }}
                    >
                      Mortgage Payoff
                    </Box>
                  </Tooltip>
                </Typography>
                <SignedMoney value={-mortgagePayoffTotal} />

                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Divider />
                </Box>

                <Typography variant="body2">= Net Cash</Typography>
                <SignedMoney value={netCashAfterMortgage} emphasize />
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1.25, display: "block" }}
              >
                Taxes: Federal {formatCurrency(federalLtcgTax)} | Oregon{" "}
                {formatCurrency(oregonCapitalGainsTax)} | NIIT{" "}
                {formatCurrency(niitTax)}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                <Tooltip
                  title={
                    "Gain for tax is generally (amount realized - adjusted basis). Amount realized is sale price minus selling costs. Adjusted basis is purchase price plus improvements (and other adjustments not modeled here)."
                  }
                  placement="top"
                  arrow
                >
                  <Box
                    component="span"
                    sx={{ textDecoration: "underline dotted" }}
                  >
                    Gain Equation (For Tax)
                  </Box>
                </Tooltip>
                : Amount Realized {formatCurrency(amountRealized)} - Adjusted
                Basis {formatCurrency(adjustedBasis)} = Estimated Gain{" "}
                {formatCurrency(gain)}
              </Typography>
              {rawGain < 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  Raw gain is negative ({formatCurrency(rawGain)}); tool clamps
                  taxable gain to {formatCurrency(0)}.
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Note: Basis is used for tax gain; it is not subtracted from your
                net proceeds as a cash expense.
              </Typography>
            </Box>
          </Box>

          {!saleInputs.reinvest1031 && gain === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Box component="span">
                <Tooltip
                  title={
                    "Estimated gain = amount realized - adjusted basis. Amount realized is sale price minus selling costs. Adjusted basis is purchase price plus improvements (and other adjustments not modeled here)."
                  }
                  placement="top"
                  arrow
                >
                  <Box
                    component="span"
                    sx={{ textDecoration: "underline dotted" }}
                  >
                    Estimated gain
                  </Box>
                </Tooltip>{" "}
                is {formatCurrency(0)} because Amount Realized does not exceed
                Adjusted Basis. If you expect capital gains tax, increase Sale
                Price or adjust Basis/Improvements.
              </Box>
            </Alert>
          )}

          {!saleInputs.reinvest1031 && gain > 0 && federalLtcgTax === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Federal LTCG tax is {formatCurrency(0)} with a positive gain. This
              usually means your Tax Profile has low taxable income and/or a
              filing status that keeps you in the 0% LTCG bracket. Check the
              selected Tax Profile.
            </Alert>
          )}

          {saleInputs.reinvest1031 && exchangeTimeline && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                1031 Timeline (No Enforcement)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                This section shows common IRS deadlines for identification (45
                days) and completion (180 days). This tool does not validate
                eligibility.
              </Typography>

              {(exchangeTimeline.isPastIdentification ||
                exchangeTimeline.isPastCompletion) && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  Today is past one or more 1031 deadlines based on the sale
                  date.
                </Alert>
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    45-Day Identification Deadline
                  </Typography>
                  <Typography sx={{ fontWeight: 900 }}>
                    {formatLocalYmd(exchangeTimeline.identificationDeadline)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    180-Day Completion Deadline
                  </Typography>
                  <Typography sx={{ fontWeight: 900 }}>
                    {formatLocalYmd(exchangeTimeline.completionDeadline)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            Projections
          </Typography>

          <Tabs
            value={projectionInputs.mode}
            onChange={(_, value) => {
              setProjectionInputs((prev) => ({
                ...prev,
                mode: value as ProjectionInputs["mode"],
              }));
            }}
            sx={{ mb: 2 }}
          >
            <Tab value="renting" label="Renting" />
            <Tab value="buying" label="Buying" />
          </Tabs>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md:
                  projectionInputs.mode === "renting"
                    ? "1.2fr 0.85fr 0.75fr 0.85fr 1.2fr"
                    : "1fr 1fr 1fr 1fr",
              },
              gap: 2,
            }}
          >
            {projectionInputs.mode === "renting" ? (
              <Tooltip
                title="Expected monthly rent payment used in Renting projection."
                arrow
                placement="top"
              >
                <TextField
                  size="small"
                  label="Average Rent (Monthly)"
                  type="number"
                  inputProps={{ step: "0.01", inputMode: "decimal" }}
                  value={String(projectionInputs.avgRent)}
                  onChange={(e) =>
                    setProjectionInputs((prev) => ({
                      ...prev,
                      avgRent: parseNumber(e.target.value, prev.avgRent),
                    }))
                  }
                />
              </Tooltip>
            ) : (
              <Tooltip
                title="Loan program preset. Selecting one auto-fills APR and mortgage term; you can still edit APR/term manually."
                arrow
                placement="top"
              >
                <FormControl fullWidth size="small">
                  <InputLabel id="loan-type-label">Loan Type</InputLabel>
                  <Select
                    labelId="loan-type-label"
                    label="Loan Type"
                    value={projectionInputs.loanTypeId}
                    onChange={(e) => {
                      const loanTypeId = String(e.target.value);
                      const preset = LOAN_TYPE_OPTIONS.find(
                        (option) => option.id === loanTypeId
                      );
                      setProjectionInputs((prev) => ({
                        ...prev,
                        loanTypeId,
                        mortgageAprPct:
                          preset && preset.id !== "custom"
                            ? preset.aprPct
                            : prev.mortgageAprPct,
                        mortgageTermYears:
                          preset && preset.id !== "custom"
                            ? preset.termYears
                            : prev.mortgageTermYears,
                      }));
                    }}
                  >
                    {LOAN_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                        {option.id !== "custom"
                          ? ` (${option.termYears}y, Rate ${option.noteRatePct}%, APR ${option.aprPct}%)`
                          : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Tooltip>
            )}
            {projectionInputs.mode === "renting" ? null : (
              <Tooltip
                title={`Annual mortgage interest rate (APR). ${
                  selectedLoanType.id === "custom"
                    ? "Custom mode."
                    : `Preset from ${selectedLoanType.label}.`
                } Used to compute monthly principal and interest.`}
                arrow
                placement="top"
              >
                <TextField
                  size="small"
                  label="Mortgage APR %"
                  value={String(projectionInputs.mortgageAprPct)}
                  type="number"
                  inputProps={{ step: "0.001", inputMode: "decimal" }}
                  onChange={(e) =>
                    setProjectionInputs((prev) => ({
                      ...prev,
                      mortgageAprPct: parseRatePercent(
                        e.target.value,
                        prev.mortgageAprPct
                      ),
                    }))
                  }
                />
              </Tooltip>
            )}
            <Tooltip
              title="Target purchase price used to calculate loan amount and down payment."
              arrow
              placement="top"
            >
              <TextField
                size="small"
                label="House Price"
                type="number"
                inputProps={{ step: "0.01", inputMode: "decimal" }}
                value={String(projectionInputs.homePrice)}
                onChange={(e) =>
                  setProjectionInputs((prev) => ({
                    ...prev,
                    homePrice: parseNumber(e.target.value, prev.homePrice),
                  }))
                }
              />
            </Tooltip>
            <Tooltip
              title="Percent of house price paid upfront. Down Payment (calc) = House Price × Down Payment %."
              arrow
              placement="top"
            >
              <TextField
                size="small"
                label="Down Payment %"
                type="number"
                inputProps={{ step: "0.01", inputMode: "decimal" }}
                value={String(
                  Math.round(projectionInputs.downPaymentPct * 10000) / 100
                )}
                onChange={(e) =>
                  setProjectionInputs((prev) => ({
                    ...prev,
                    downPaymentPct: clampNumber(
                      parseNumber(e.target.value, prev.downPaymentPct * 100) /
                        100,
                      0,
                      1
                    ),
                  }))
                }
              />
            </Tooltip>
            <Tooltip
              title="Calculated field: House Price × Down Payment %."
              arrow
              placement="top"
            >
              <TextField
                size="small"
                label="Down Payment (calc)"
                value={formatCurrency(downPaymentGoal)}
                InputProps={{ readOnly: true }}
              />
            </Tooltip>

            <Tooltip
              title={`Based on next month: ${formatCurrency(
                autoMonthlyExpenses
              )} (from ${expenses.length} expenses: unpaid + unskipped, due in within 30 days).`}
              arrow
              placement="top"
            >
              <FormControl fullWidth size="small">
                <InputLabel id="monthly-expenses-mode">
                  Monthly Expenses
                </InputLabel>
                <Select
                  labelId="monthly-expenses-mode"
                  label="Monthly Expenses"
                  value={
                    projectionInputs.useAutoMonthlyExpenses ? "auto" : "manual"
                  }
                  onChange={(e) =>
                    setProjectionInputs((prev) => ({
                      ...prev,
                      useAutoMonthlyExpenses: String(e.target.value) === "auto",
                    }))
                  }
                >
                  <MenuItem value="auto">
                    Auto (
                    {expensesLoading
                      ? "loading…"
                      : formatCurrency(autoMonthlyExpenses)}
                    )
                  </MenuItem>
                  <MenuItem value="manual">Manual override</MenuItem>
                </Select>
              </FormControl>
            </Tooltip>

            {projectionInputs.mode === "buying" && (
              <>
                <Tooltip
                  title="Mortgage duration in years. Used in amortization payment formula."
                  arrow
                  placement="top"
                >
                  <TextField
                    size="small"
                    label="Mortgage Term (Years)"
                    type="number"
                    inputProps={{ step: "0.01", inputMode: "decimal" }}
                    value={String(projectionInputs.mortgageTermYears)}
                    onChange={(e) =>
                      setProjectionInputs((prev) => ({
                        ...prev,
                        mortgageTermYears: clampNumber(
                          parseNumber(e.target.value, prev.mortgageTermYears),
                          1,
                          50
                        ),
                      }))
                    }
                  />
                </Tooltip>
                <Tooltip
                  title="County/city annual effective property tax rate. Monthly tax = House Price × Rate ÷ 12."
                  arrow
                  placement="top"
                >
                  <TextField
                    size="small"
                    label="Property Tax Rate % (Annual)"
                    type="number"
                    inputProps={{ step: "0.01", inputMode: "decimal" }}
                    value={String(projectionInputs.propertyTaxRatePct)}
                    onChange={(e) =>
                      setProjectionInputs((prev) => ({
                        ...prev,
                        propertyTaxRatePct: clampNumber(
                          parseNumber(e.target.value, prev.propertyTaxRatePct),
                          0,
                          25
                        ),
                      }))
                    }
                    helperText={`Monthly: ${formatCurrency(monthlyPropertyTax)}`}
                  />
                </Tooltip>
                <Tooltip
                  title="Annual homeowners insurance premium. Monthly insurance = Annual premium ÷ 12."
                  arrow
                  placement="top"
                >
                  <TextField
                    size="small"
                    label="Homeowners Insurance (Annual)"
                    type="number"
                    inputProps={{ step: "0.01", inputMode: "decimal" }}
                    value={String(projectionInputs.homeownersInsuranceAnnual)}
                    onChange={(e) =>
                      setProjectionInputs((prev) => ({
                        ...prev,
                        homeownersInsuranceAnnual: Math.max(
                          0,
                          parseNumber(
                            e.target.value,
                            prev.homeownersInsuranceAnnual
                          )
                        ),
                      }))
                    }
                    helperText={`Monthly: ${formatCurrency(
                      monthlyHomeownersInsurance
                    )}`}
                  />
                </Tooltip>
                <Tooltip
                  title="Annual PMI rate for the loan. Applied only when down payment is below 20%."
                  arrow
                  placement="top"
                >
                  <TextField
                    size="small"
                    label="PMI Rate % (Annual)"
                    type="number"
                    inputProps={{ step: "0.01", inputMode: "decimal" }}
                    value={String(projectionInputs.pmiRatePct)}
                    onChange={(e) =>
                      setProjectionInputs((prev) => ({
                        ...prev,
                        pmiRatePct: clampNumber(
                          parseNumber(e.target.value, prev.pmiRatePct),
                          0,
                          25
                        ),
                      }))
                    }
                    helperText={
                      requiresPmi
                        ? `Monthly: ${formatCurrency(monthlyPmi)}`
                        : "Not applied at >= 20% down"
                    }
                  />
                </Tooltip>
                <Tooltip
                  title="Monthly HOA dues from the property/community association."
                  arrow
                  placement="top"
                >
                  <TextField
                    size="small"
                    label="HOA (Monthly)"
                    type="number"
                    inputProps={{ step: "0.01", inputMode: "decimal" }}
                    value={String(projectionInputs.hoaMonthly)}
                    onChange={(e) =>
                      setProjectionInputs((prev) => ({
                        ...prev,
                        hoaMonthly: Math.max(
                          0,
                          parseNumber(e.target.value, prev.hoaMonthly)
                        ),
                      }))
                    }
                  />
                </Tooltip>
              </>
            )}
            {!projectionInputs.useAutoMonthlyExpenses && (
              <Tooltip
                title="Manual monthly expenses value used when Monthly Expenses is set to Manual override."
                arrow
                placement="top"
              >
                <TextField
                  size="small"
                  label="Monthly Expenses Override"
                  type="number"
                  inputProps={{ step: "0.01", inputMode: "decimal" }}
                  value={String(projectionInputs.monthlyExpensesOverride)}
                  onChange={(e) =>
                    setProjectionInputs((prev) => ({
                      ...prev,
                      monthlyExpensesOverride: parseNumber(
                        e.target.value,
                        prev.monthlyExpensesOverride
                      ),
                    }))
                  }
                />
              </Tooltip>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
                lg:
                  projectionInputs.mode === "buying"
                    ? "repeat(5, 1fr)"
                    : "repeat(4, 1fr)",
              },
              gap: 2,
            }}
          >
            <Box>
              <Tooltip
                title={`Net from sale of property = net proceeds (${formatCurrency(
                  netProceeds
                )}) - mortgage payoff (${formatCurrency(mortgagePayoffTotal)})`}
                placement="top"
                arrow
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textDecoration: "underline dotted", cursor: "help" }}
                >
                  Net from Sale of Property
                </Typography>
              </Tooltip>
              <Typography sx={{ fontWeight: 900 }}>
                {formatCurrency(netFromSale)}
              </Typography>
            </Box>
            {projectionInputs.mode === "renting" ? (
              <>
                <Box>
                  <Tooltip
                    title={`Months of rent covered = net from sale (${formatCurrency(
                      netFromSale
                    )}) / avg rent (${formatCurrency(
                      projectionInputs.avgRent
                    )})`}
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textDecoration: "underline dotted",
                        cursor: "help",
                      }}
                    >
                      Months of Rent Covered
                    </Typography>
                  </Tooltip>
                  <Typography sx={{ fontWeight: 900 }}>
                    {Number.isFinite(monthsOfRent)
                      ? monthsOfRent.toFixed(1)
                      : "-"}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip
                    title={`Monthly savings toward down payment = take-home (${formatCurrency(
                      monthlyTakeHome
                    )}) - expenses (${formatCurrency(
                      effectiveMonthlyExpenses
                    )}) - rent (${formatCurrency(projectionInputs.avgRent)})`}
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textDecoration: "underline dotted",
                        cursor: "help",
                      }}
                    >
                      Monthly Savings Toward Down Payment
                    </Typography>
                  </Tooltip>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: signedColor(monthlyNetSavingsRenting),
                    }}
                  >
                    {formatSignedCurrency(monthlyNetSavingsRenting)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Time to Down Payment Goal
                  </Typography>
                  <Typography sx={{ fontWeight: 900 }}>
                    {timeToDownPayment === null
                      ? "N/A (monthly net ≤ 0)"
                      : `${timeToDownPayment.years} years, ${timeToDownPayment.months} months`}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box>
                  <Tooltip
                    title={
                      requiresPmi
                        ? `PMI = loan amount (${formatCurrency(
                            loanAmount
                          )}) * ${projectionInputs.pmiRatePct}% / 12`
                        : "No PMI applied because down payment is at least 20%."
                    }
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textDecoration: "underline dotted",
                        cursor: "help",
                      }}
                    >
                      Monthly PMI
                    </Typography>
                  </Tooltip>
                  <Typography sx={{ fontWeight: 900 }}>
                    {formatCurrency(monthlyPmi)}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip
                    title={`Mortgage payment (P&I) is estimated using loan amount (${formatCurrency(
                      loanAmount
                    )}), APR (${projectionInputs.mortgageAprPct}%), and term (${projectionInputs.mortgageTermYears} years).`}
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textDecoration: "underline dotted",
                        cursor: "help",
                      }}
                    >
                      Monthly Mortgage (P&I)
                    </Typography>
                  </Tooltip>
                  <Typography sx={{ fontWeight: 900 }}>
                    {formatCurrency(monthlyMortgagePayment)}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip
                    title={`24-month reserve = (P&I + tax + insurance + PMI + HOA) * 24 = (${formatCurrency(
                      monthlyHousingPaymentBuying
                    )}) * 24`}
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textDecoration: "underline dotted",
                        cursor: "help",
                      }}
                    >
                      24-Month Mortgage Reserve
                    </Typography>
                  </Tooltip>
                  <Typography sx={{ fontWeight: 900 }}>
                    {formatCurrency(mortgageReserve24Months)}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip
                    title={`Monthly savings toward down payment = take-home (${formatCurrency(
                      monthlyTakeHome
                    )}) - expenses (${formatCurrency(
                      effectiveMonthlyExpenses
                    )}) - housing [P&I + tax + insurance + PMI + HOA] (${formatCurrency(
                      monthlyHousingPaymentBuying
                    )})`}
                    placement="top"
                    arrow
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        textDecoration: "underline dotted",
                        cursor: "help",
                      }}
                    >
                      Monthly Savings Toward Down Payment
                    </Typography>
                  </Tooltip>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: signedColor(monthlyNetSavingsBuying),
                    }}
                  >
                    {formatSignedCurrency(monthlyNetSavingsBuying)}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            Sources (Authoritative)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            These are the sources used for federal capital gains thresholds,
            1031 rules, and Oregon income tax context.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {sources.map((s) => (
              <Link key={s.href} href={s.href} target="_blank" rel="noreferrer">
                {s.label}
              </Link>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() => {
            const next: RealEstateAsset = {
              id: generateId(),
              name: "New Asset",
              address: "",
              acres: 0,
              purchaseDate: "2020-05-01",
              purchasePrice: 0,
              improvements: 0,
            };
            setAssets((prev) => [...prev, next]);
            setSelectedAssetId(next.id);
          }}
        >
          Add Asset
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            if (!selectedAssetId) return;
            updateAsset({ ...DEFAULT_SUNRIVER_ASSET });
            setSaleInputs((prev) => ({ ...prev, salePrice: 100000 }));
          }}
        >
          Reset Sunriver Defaults
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={assets.length <= 1}
          onClick={() => {
            setAssets((prev) => {
              const nextAssets = prev.filter((a) => a.id !== selectedAssetId);
              const nextId = nextAssets[0]?.id || "";
              setSelectedAssetId(nextId);
              return nextAssets;
            });
          }}
        >
          Delete Asset
        </Button>
      </Box>

      <PageShareModal
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        pageType="housing"
        payload={{
          version: 1,
          settings: buildCurrentHousingSettings(),
          taxProfile: selectedTaxProfile
            ? {
                name: selectedTaxProfile.name,
                incomeInfo: selectedTaxProfile.incomeInfo,
              }
            : null,
        }}
        label={
          housingProfiles.find((p) => p.id === housingProfileId)?.name ||
          "Housing Forecast"
        }
        buildShareUrl={(shareId) =>
          `${window.location.origin}/shared/housing-forecast?shareId=${shareId}`
        }
      />
    </Box>
  );
};

export default HousingForecast;
